/**
 * HomeLanding — the unified Home tab.
 *
 * Bill 2026-05-13 (v0.14.43) — restructured to canonical SPEC Screen 32:
 *
 *   1. GreetingStrip      — greeting + date + branch chip + officer code
 *   2. QuickActionsRow    — 4 SPEC pills: Start new cart · Resume draft ·
 *                           Search customer · Find task
 *   3. MetricCardsRow     — 4-col canonical metric grid (MY CARTS /
 *                           TODAY'S APPTS / TASKS / APPROVALS) with
 *                           click-through drill routes per SPEC
 *   4. Recent activity    — 3-up legacy widget grid (MyTasks + MyCarts +
 *                           MyAppointments) retained as detail panel
 *                           below the canonical metric row. These can be
 *                           retired when /v3/home/dashboard ships with
 *                           full role-additive payloads.
 *   5. Supervisor row     — TeamActivity (Supervisor+)
 *   6. Manager row        — BranchKpis (Manager+)
 *   7. Executive row      — SystemHealth (Executive / SuperAdmin)
 *   8. Info banner        — audit-03 consolidation + admin-config explainer
 *
 * Role gating:
 *   All users:        Greeting + QuickActions + Metrics + Recent activity
 *   Supervisor+:      + TeamActivity
 *   Manager+:         + BranchKpis
 *   Executive+:       + SystemHealth
 *   Admin/SuperAdmin: redirected to /admin (handled by RoleLanding wrapper).
 */
import { useAuth } from '@/contexts/AuthContext';
import type { UserRole } from '@retail-ops/shared-types/enums';
import { QuickActionsRow } from './widgets/QuickActionsRow';
import { MyFollowUpTasksWidget } from './widgets/MyFollowUpTasksWidget';
import { MyCartsWidget } from './widgets/MyCartsWidget';
import { UnclaimedOnlineCartsWidget } from './widgets/UnclaimedOnlineCartsWidget';
import './styles/home.css';

const SUPERVISOR_PLUS: ReadonlyArray<UserRole> = [
  'Supervisor',
  'Manager',
  'Executive',
  'SuperAdmin',
  'Admin',
];

const MANAGER_PLUS: ReadonlyArray<UserRole> = [
  'Manager',
  'Executive',
  'SuperAdmin',
  'Admin',
];

const EXECUTIVE_PLUS: ReadonlyArray<UserRole> = [
  'Executive',
  'SuperAdmin',
];

export function HomeLanding(): JSX.Element {
  const { user } = useAuth();
  const role: UserRole = user?.primaryRole ?? 'Officer';


  return (
    <div className="home-page">
      {/* PILOT 2026-09-11 (Bill): the greeting strip ("Good morning · Friday,
          September 11") is off. A salutation and today's date carry no
          information an FSR does not already have, and the strip only earns
          its height once tasks and time-bound items render inside it. Until
          those are wired and confirmed, it is decoration above the fold. */}

      {/* Bill 2026-07-13 Item 5: prominent New Account / Find Customer /
          Account Maintenance actions. */}
      <QuickActionsRow />

      {/* PILOT 2026-09-11 (Bill): YOUR METRICS is off the landing. Three tiles
          of zeros above the fold are noise on day one of a pilot, and the space
          is worth more as room for the two lists an FSR actually works from —
          My Carts and Unclaimed online carts. HomeKpiRow is untouched; restore
          the line below when the numbers mean something. */}

      {/* Open carts · Open tasks · Unclaimed online carts (Item 5). */}
      {/* PILOT 2026-09-04: MyTasksWidget removed — it deep-links into the Tasks
          module (/rtm), which is hidden for the pilot because it is not wired
          end to end for Phase 1. Open carts and unclaimed online applications
          are both in scope and stay. */}
      {/* 2026-10-16 (MacKenzie Mott; Bill approved): My Open Tasks is back on Home,
          rebuilt on the follow-up tasks the account-opening pipeline creates for the
          officer (dbo.FollowUpTasks). Rows open the cart or customer page — no /rtm. */}
      <div className="home-grid home-grid--primary">
        <MyCartsWidget />
        <MyFollowUpTasksWidget />
        <UnclaimedOnlineCartsWidget />
      </div>

      {/* PILOT 2026-09-11: Team Activity, Branch KPIs AND System Health are all
          gone from Home. The first two rendered MOCK constants - "4 in lobby
          now", "7 accounts opened +2" - next to real, hard-won zeros in YOUR
          METRICS, which teaches an FSR to distrust both.

          System Health went too (Bill): its only affordance was an "ADMIN >"
          link into an admin operations tree - Reporting / Kill List / Audit /
          Notifications / Records Mgmt - living inside ROP-teams. That tree is
          a standing rule violation on its own (ALL admin/config UI belongs in
          the admin-panel SWA, never ROP-teams) and it is certainly not
          something a pilot FSR should be one click from. Platform status now
          lives where a user would look for it: Help -> About. */}
    </div>
  );
}

export default HomeLanding;
